import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme, Box } from '@mui/material';
import NavBar from './components/NavBar';
import { swarmApi } from './api';
import Sidebar from './components/Sidebar';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import Services from './components/Services';
import Certificates from './components/Certificates';
import Monitoring from './components/Monitoring';
import Tests from './components/Tests';
import Notifications from './components/Notifications';
import Users from './components/Users';
import AuditLog from './components/AuditLog';
import Domains from './components/Domains';
import Networks from './components/Networks';
import Containers from './components/Containers';
import Nodes from './components/Nodes';
import Cleanup from './components/Cleanup';
import AiGovernance from './components/AiGovernance';
import IntentConsole from './components/IntentConsole';
import { LiveEventsProvider } from './components/LiveEventFeed';
import PolicyAdmin from './components/PolicyAdmin';
import SecretsAdmin from './components/SecretsAdmin';

function App() {
  const [auth, setAuth] = useState(!!localStorage.getItem('token'));
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  });

  const [observabilityStatus, setObservabilityStatus] = useState({ grafanaOnline: false, kibanaOnline: false });

  React.useEffect(() => {
    document.title = user?.company?.name || process.env.REACT_APP_APP_NAME || 'CPT Swarm';
  }, [user]);

  React.useEffect(() => {
    if (auth) {
      const fetchStatus = () => {
        swarmApi.getObservabilityStatus()
          .then(res => setObservabilityStatus(res.data))
          .catch(err => console.warn('Observability check failed', err));
      };
      fetchStatus();
      const interval = setInterval(fetchStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [auth]);

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: '#1976d2' },
      secondary: { main: '#dc004e' },
    },
  });

  const handleLogin = (userData) => {
    setAuth(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuth(false);
    setUser(null);
  };

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
  };

  const drawerWidth = sidebarOpen ? 240 : 0;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {auth ? (
          <LiveEventsProvider enabled={auth}>
          <Box sx={{ display: 'flex' }}>
            <NavBar
              auth={auth}
              onLogout={handleLogout}
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
              sidebarOpen={sidebarOpen}
              toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
              user={user}
            />
            <Sidebar open={sidebarOpen} user={user} onClose={() => setSidebarOpen(false)} observabilityStatus={observabilityStatus} />
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                p: 3,
                mt: 8,
                transition: 'all 0.2s',
                minHeight: '100vh',
                width: 0,
              }}
            >
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/services" element={<Services />} />
                <Route path="/certificates" element={<Certificates />} />
                <Route path="/monitoring" element={<Monitoring observabilityStatus={observabilityStatus} />} />
                <Route path="/tests" element={<Tests user={user} />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/networks" element={<Networks />} />
                <Route path="/containers" element={<Containers />} />
                <Route path="/cleanup" element={<Cleanup />} />
                <Route path="/nodes" element={<Nodes />} />
                <Route path="/ai" element={<AiGovernance user={user} />} />
                <Route path="/intent" element={<IntentConsole user={user} />} />
                {user?.role === 'admin' && <Route path="/users" element={<Users />} />}
                {user?.role === 'admin' && <Route path="/audit" element={<AuditLog />} />}
                {user?.role === 'admin' && <Route path="/domains" element={<Domains />} />}
                {user?.role === 'admin' && <Route path="/policy" element={<PolicyAdmin />} />}
                {user?.role === 'admin' && <Route path="/secrets" element={<SecretsAdmin />} />}
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
            </Box>
          </Box>
          </LiveEventsProvider>
        ) : (
          <Routes>
            <Route path="/login" element={<Auth onLogin={handleLogin} />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        )}
      </Router>
    </ThemeProvider>
  );
}

export default App;
